import { useState } from 'react';
import { getHook, InternalHookState } from './hook';
import type { AsyncModal } from './types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- imported to make the @links work in JSDoc
import type { Configuration, Controls } from './types';

/**
 * This is a hook that can be used to power modals.
 *
 * ***When to use it***: Use this hook for displaying modals with validation and saving phases. The
 * hook will manage the state of the modal for you through callbacks you provide to `show(options)`.
 * This is best in cases where you have a model that you want to edit and save wholesale. It can be
 * cumbersome in cases where your modal wants to perform actions other than whole-sale saving of the
 * model.
 *
 * ***What it does***: You provide an initial model and a save handler (and other possible options),
 * and it maintains state of a local model while the modal is open. The `save()` callback it
 * provides will validate the model, invoke your save handler, and handle lifecycle around success,
 * errors, and auto-closing. Your save handler receives the entire model, so you do not have
 * visibility into changes without comparing the initial model and saving model manually.
 *
 * @see {@link Configuration}: options you can pass to `show()` for difference use cases
 *
 * @see {@link Controls}: the controls available to the modal when it is showing
 *
 * @see {@link AsyncModal}: the return type of the hook, which is a union of open and closed types.
 *
 * @see useAsyncModal.stories.tsx for live examples of common use cases.
 */
export function useAsyncModal<T>(): AsyncModal<T> {
  // showState === null: the modal is closed
  // showState !== null: the modal is open and controls are available.
  const [showState, setShowState] = useState<InternalHookState<T> | null>( null );

  return getHook( showState, setShowState );
}
